// Starts the box at a usable size on hosts that strip the resize script.
const START_HEIGHT = 720

function attribute(value: string) {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

export function embedAddress({ embedUrl, query }: { embedUrl: string; query: string }) {
  return query ? `${embedUrl}?${query}` : embedUrl
}

export function embedCode({
  address,
  resizeScriptUrl,
  title,
  height,
}: {
  address: string
  resizeScriptUrl: string
  title: string
  height: number | null
}) {
  const iframe = `<iframe
  src="${attribute(address)}"
  title="${attribute(title)}"
  style="width: 100%; height: ${height ?? START_HEIGHT}px; border: 0;"
  allow="geolocation; clipboard-write; web-share"
  loading="lazy"
  data-supporterbase-embed
></iframe>`

  if (height !== null) return iframe

  return `${iframe}
<script src="${attribute(resizeScriptUrl)}" async></script>`
}

export type RsvpFields = {
  show_address_fields: boolean
  require_address_fields: boolean
  show_phone_field: boolean
  require_phone_field: boolean
  allow_guests: boolean
}

export const RSVP_FIELD_NAMES = [
  'show_address_fields',
  'require_address_fields',
  'show_phone_field',
  'require_phone_field',
  'allow_guests',
] as const satisfies ReadonlyArray<keyof RsvpFields>

export const RSVP_FIELD_REQUIRES: Partial<Record<keyof RsvpFields, keyof RsvpFields>> = {
  require_address_fields: 'show_address_fields',
  require_phone_field: 'show_phone_field',
}

export function rsvpFieldOn(fields: RsvpFields, name: keyof RsvpFields) {
  const parent = RSVP_FIELD_REQUIRES[name]
  return fields[name] && (!parent || fields[parent])
}

// Every field is written, as the form's defaults differ from the builder's.
export function rsvpFormQuery(fields: RsvpFields) {
  return new URLSearchParams(RSVP_FIELD_NAMES.map((name) => [name, String(rsvpFieldOn(fields, name))])).toString()
}

// Liquid's `default` treats false as empty, so a fixed field can only give way to a page's requirement.
const PAGE_REQUIREMENT: Partial<Record<keyof RsvpFields, string>> = {
  show_address_fields: 'require_address?',
  require_address_fields: 'require_address?',
  show_phone_field: 'require_phone?',
  require_phone_field: 'require_phone?',
}

const PAGE_FIELD: Record<keyof RsvpFields, string> = {
  show_address_fields: 'is_address?',
  require_address_fields: 'require_address?',
  show_phone_field: 'is_phone?',
  require_phone_field: 'require_phone?',
  allow_guests: 'allow_guests?',
}

function fixedFieldValue(name: keyof RsvpFields, on: boolean) {
  const requirement = PAGE_REQUIREMENT[name]
  if (on || !requirement) return String(on)
  return `{% if page[page.type_slug].${requirement} %}true{% else %}false{% endif %}`
}

export function nationbuilderTemplateQuery(fixed: RsvpFields | null) {
  return RSVP_FIELD_NAMES.map((name) => {
    const value = fixed
      ? fixedFieldValue(name, rsvpFieldOn(fixed, name))
      : `{{ page[page.type_slug].${PAGE_FIELD[name]} }}`
    return `${name}=${value}`
  }).join('&')
}

export const NATIONBUILDER_GROUP_TAG = 'admin_supporter_group'
export const NATIONBUILDER_PARTIAL = 'partial_supporterbase_embedded_form'

export function nationbuilderConditionalSnippet({ about, placeholder }: { about: string; placeholder: string }) {
  return `{% comment %} ${about} {% endcomment %}
{% capture calendar_tags %}{{ page.parent.tags | map: 'slug' | join: '#' }}{% endcapture %}
{% if calendar_tags contains "${NATIONBUILDER_GROUP_TAG}" %}
  {% include '${NATIONBUILDER_PARTIAL}' %}
{% else %}
  {% comment %} ${placeholder} {% endcomment %}
{% endif %}`
}
